<template>
  <FieldShell :id="inputId"
    :label="shellLabel"
    :required="required"
    :icon="iconClass"
    :unit="unitText"
    :hint="hint"
    :error="shownError"
    :invalid="ringed"
    :count="count"
    :size="size"
    :shape="preset.shape"
    :disabled="disabled"
    :readonly="readonly"
    :class="phase && `field--${phase}`"
    @focusin="phase === 'saved' && (phase = '')"
    @focusout="touched = true">
    <template v-if="$slots['label-end']"
      #label-end><slot name="label-end" /></template>
    <template v-if="$slots.action"
      #action><slot name="action" /></template>
    <template v-if="$slots.hint"
      #hint><slot name="hint" /></template>
    <!-- A custom control (e.g. CategorySelect) goes in the default slot and takes `id` for the label. -->
    <slot :id="inputId">
      <component :is="preset.input"
        :id="inputId"
        ref="input"
        v-bind="inputProps"
        :model-value="statusText ? '' : modelValue"
        @update:model-value="$emit('update:modelValue', $event)"
        @update:color-images="$emit('update:colorImages', $event)" />
    </slot>
  </FieldShell>
</template>

<script>
import FieldShell from './FieldShell.vue'
import TextInput from './inputs/TextInput.vue'
import TextArea from './inputs/TextArea.vue'
import SelectInput from './inputs/SelectInput.vue'
import ChoiceInput from './inputs/ChoiceInput.vue'
import DateInput from './inputs/DateInput.vue'
import SwitchInput from './inputs/SwitchInput.vue'
import UploadField from '../upload/UploadField.vue'
import { PRESETS, INPUT_PROPS } from './presets'
import * as validate from '@/lib/validate'

// Types whose value is a number, checked against `min` / `max`.
const NUMERIC = ['number', 'decimal', 'price', 'percent']

let uid = 0

export default {
  name: 'form-field',
  components: { FieldShell, TextInput, TextArea, SelectInput, ChoiceInput, DateInput, SwitchInput, UploadField },
  emits: ['update:modelValue', 'update:colorImages'],
  // Its EditForm asks it to check itself before saving, and names the field a save was refused for (by `name`).
  inject: { formFields: { default: null }, formInvalid: { default: null } },
  props: {
    modelValue: { type: [String, Number, Boolean, Array, null], default: '' },
    // Declared so v-model.number / .trim work without the object landing on the element.
    modelModifiers: { type: Object, default: () => ({}) },
    type: { type: String, default: 'text' },
    label: { type: String, default: '' },
    // A Font Awesome class; null takes the type's own icon, '' shows none.
    icon: { type: String, default: null },
    unit: { type: String, default: null },
    hint: { type: String, default: '' },
    error: { type: String, default: '' },
    // The error ring without a message, for a field whose message is shown elsewhere.
    invalid: { type: Boolean, default: false },
    size: { type: String, default: 'md' },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    placeholder: { type: String, default: undefined },
    name: { type: String, default: undefined },
    autocomplete: { type: String, default: undefined },
    inputmode: { type: String, default: undefined },
    min: { type: [Number, String], default: undefined },
    max: { type: [Number, String], default: undefined },
    step: { type: [Number, String], default: undefined },
    maxlength: { type: [Number, String], default: undefined },
    rows: { type: [Number, String], default: undefined },
    autogrow: { type: Boolean, default: false },
    options: { type: Array, default: undefined },
    labelKey: { type: String, default: undefined },
    valueKey: { type: String, default: undefined },
    clearable: { type: Boolean, default: undefined },
    loading: { type: Boolean, default: false },
    // While its value is being saved: a spinner for the icon and, where the input has a placeholder, the text in place
    // of the value; a save without an error then shows a green check and "Salvestatud" for a second.
    saving: { type: Boolean, default: false },
    // checkbox: the text inside the switch when there is no label, e.g. in a table row.
    title: { type: String, default: '' },
    // checkbox: no empty line above, for a switch that is not in a row of labelled fields.
    labelSide: { type: Boolean, default: false },
    // For a field without a visible label; it goes to the control, not the wrapper.
    ariaLabel: { type: String, default: undefined },
    colorImages: { type: Array, default: undefined },
    colorOptions: { type: Array, default: undefined },
    // A rule of the form's own on a filled value, as the type's are (lib/validate.js): the problem, or '' when fine.
    rule: { type: Function, default: null },
  },
  data() {
    // A problem shows once the field has been left, or when its form tries to save.
    // `phase` is the saving state shown: 'saving', 'saved' or ''.
    return { inputId: `field-${++uid}`, touched: false, phase: '' }
  },
  computed: {
    preset() {
      return { shape: 'frame', ...(PRESETS[this.type] || PRESETS.text) }
    },
    // The type's format rule on a filled value; `required` only where Field draws the control itself.
    problem() {
      const v = this.modelValue
      if (v == null || v === '') return this.required && !this.$slots.default ? 'Kohustuslik väli' : ''
      if (NUMERIC.includes(this.type)) {
        const range = validate.range(v, this.min, this.max)
        if (range) return range
      }
      return this.preset.check?.(v) || this.rule?.(v) || ''
    },
    shownError() {
      return this.error || (this.touched ? this.problem : '')
    },
    ringed() {
      return this.invalid || !!this.shownError || (!!this.name && this.formInvalid?.field === this.name)
    },
    iconClass() {
      if (this.phase === 'saving') return 'fa-solid fa-spinner fa-spin'
      if (this.phase === 'saved') return 'fa-solid fa-check'
      return this.icon ?? this.preset.icon ?? ''
    },
    statusText() {
      if (!INPUT_PROPS[this.preset.input].includes('placeholder')) return ''
      return { saving: 'Salvestamine...', saved: 'Salvestatud' }[this.phase] || ''
    },
    unitText() {
      return this.unit ?? this.preset.unit ?? ''
    },
    shellLabel() {
      return this.preset.shape === 'switch' && this.labelSide ? '' : this.label
    },
    count() {
      return this.type === 'textarea' && this.maxlength ? `${String(this.modelValue ?? '').length} / ${this.maxlength}` : ''
    },
    inputProps() {
      const given = {
        ...this.$props,
        native: this.preset.native,
        align: this.preset.align,
        inputmode: this.inputmode ?? this.preset.inputmode,
        autocomplete: this.autocomplete ?? this.preset.autocomplete,
        text: this.label || this.title,
      }
      const props = {}
      for (const key of INPUT_PROPS[this.preset.input]) if (given[key] !== undefined) props[key] = given[key]
      if (this.statusText) props.placeholder = this.statusText
      // Held while saving, so nothing typed is overwritten by the saved value coming back.
      if (this.phase === 'saving' && INPUT_PROPS[this.preset.input].includes('readonly')) props.readonly = true
      if (this.phase === 'saving') props['aria-busy'] = 'true'
      if (this.ariaLabel) props['aria-label'] = this.ariaLabel
      if (this.shownError || this.hint) props['aria-describedby'] = this.inputId + '-hint'
      if (this.error || this.ringed) props['aria-invalid'] = 'true'
      return props
    },
  },
  watch: {
    // The parent sets its error before it clears `saving`, so an error here means the save failed. A quick save keeps
    // the spinner for a moment, so the states follow one another instead of blinking.
    saving(now) {
      clearTimeout(this.phaseTimer)
      if (now) {
        this.phase = 'saving'
        this.savingSince = Date.now()
        return
      }
      if (this.error || this.invalid) {
        this.phase = ''
        return
      }
      this.phaseTimer = setTimeout(() => {
        this.phase = 'saved'
        this.phaseTimer = setTimeout(() => { this.phase = '' }, 1000)
      }, Math.max(0, 500 - (Date.now() - this.savingSince)))
    },
    // The text fades in on each swap between the value and a status.
    statusText() {
      this.$el?.querySelector?.('.field__control')?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' })
    },
  },
  mounted() {
    this.formFields?.add(this)
  },
  beforeUnmount() {
    this.formFields?.delete(this)
    clearTimeout(this.phaseTimer)
  },
  methods: {
    // Shows its problem, if any; false holds the form's save back.
    check() {
      this.touched = true
      return !this.problem
    },
    focus() {
      this.$refs.input?.$el?.focus?.()
    },
    uploadPending() {
      return this.$refs.input?.uploadPending?.() ?? Promise.resolve(true)
    },
  },
}
</script>
